import { useState } from "react";
import toast from "react-hot-toast";
import {
  ContactRound,
  KeyRound,
  Mail,
  MapPin,
  Phone,
  Receipt,
} from "lucide-react";
import { Button } from "@/components/ui";
import { SectionCard } from "@/components/common";
import { cn } from "@/lib/utils";
import { formatDateTime } from "@/lib/datetime";
import { toastApiError } from "@/lib/errorMessages";
import type {
  AdminPasswordResetEmail,
  AdminUserContactActions,
} from "./types";

export interface AdminUserContactLabels {
  contactCard: string;
  email: string;
  sendPasswordReset: string;
  toastPasswordResetSent: string;
  passwordResetActiveLink: string;
  passwordResetCooldown: string;
  phone: string;
  contactEmail: string;
  vatNumber: string;
  billingAddress: string;
}

interface AdminUserContactCardProps {
  accountEmail: string;
  contactPhone: string | null;
  contactEmail: string | null;
  vatNumber: string | null;
  billingAddress: string | null;
  passwordResetEmail: AdminPasswordResetEmail;
  labels: AdminUserContactLabels;
  actions: AdminUserContactActions | undefined;
  className: string;
}

function passwordResetHint(
  status: AdminPasswordResetEmail,
  labels: AdminUserContactLabels,
): string | null {
  if (status.canSend || !status.blockedReason) return null;
  if (status.blockedReason === "ACTIVE_LINK" && status.activeLinkExpiresAt) {
    return labels.passwordResetActiveLink.replace(
      "{when}",
      formatDateTime(status.activeLinkExpiresAt),
    );
  }
  if (status.blockedReason === "COOLDOWN" && status.nextAllowedAt) {
    return labels.passwordResetCooldown.replace(
      "{when}",
      formatDateTime(status.nextAllowedAt),
    );
  }
  return null;
}

export function AdminUserContactCard({
  accountEmail,
  contactPhone,
  contactEmail,
  vatNumber,
  billingAddress,
  passwordResetEmail,
  labels,
  actions,
  className,
}: AdminUserContactCardProps) {
  const [isSendingReset, setIsSendingReset] = useState(false);

  const handlePasswordReset = async () => {
    if (!actions?.onSendPasswordReset) return;
    setIsSendingReset(true);
    try {
      await actions.onSendPasswordReset();
      toast.success(labels.toastPasswordResetSent);
    } catch (err: unknown) {
      toastApiError(err, "generic");
    } finally {
      setIsSendingReset(false);
    }
  };

  const canSendPasswordReset = actions != null;
  const resetHint = passwordResetHint(passwordResetEmail, labels);

  const contactFields = [
    contactPhone
      ? { key: "phone", icon: Phone, label: labels.phone, value: contactPhone }
      : null,
    contactEmail
      ? {
          key: "contactEmail",
          icon: Mail,
          label: labels.contactEmail,
          value: contactEmail,
        }
      : null,
    vatNumber
      ? {
          key: "vat",
          icon: Receipt,
          label: labels.vatNumber,
          value: vatNumber,
          mono: true,
        }
      : null,
    billingAddress
      ? {
          key: "billing",
          icon: MapPin,
          label: labels.billingAddress,
          value: billingAddress,
          wide: true,
        }
      : null,
  ].filter((field) => field != null);

  return (
    <SectionCard
      title={labels.contactCard}
      icon={ContactRound}
      className={cn("mb-8", className)}
      contentClassName="space-y-0 p-0"
    >
      <div className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <p className="text-xs font-medium text-surface-500">{labels.email}</p>
          <p className="mt-0.5 break-all text-sm font-medium text-surface-900">
            {accountEmail || "—"}
          </p>
        </div>
        {accountEmail && canSendPasswordReset ? (
          <div className="flex shrink-0 flex-col gap-1.5 sm:items-end">
            <Button
              size="sm"
              variant="outline"
              type="button"
              onClick={() => void handlePasswordReset()}
              isLoading={isSendingReset}
              disabled={!passwordResetEmail.canSend}
              leftIcon={<KeyRound className="h-4 w-4" />}
            >
              {labels.sendPasswordReset}
            </Button>
            {resetHint ? (
              <p className="max-w-xs text-xs text-surface-500 sm:text-right">
                {resetHint}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
      {contactFields.length > 0 ? (
        <dl className="grid gap-x-6 gap-y-4 border-t border-surface-200 px-5 py-4 sm:grid-cols-2">
          {contactFields.map((field) => {
            const Icon = field.icon;
            return (
              <div
                key={field.key}
                className={cn(
                  "flex min-w-0 items-start gap-2.5",
                  field.wide && "sm:col-span-2",
                )}
              >
                <Icon
                  className="mt-0.5 h-4 w-4 shrink-0 text-surface-400"
                  aria-hidden
                />
                <div className="min-w-0">
                  <dt className="text-xs font-medium text-surface-500">
                    {field.label}
                  </dt>
                  <dd
                    className={cn(
                      "mt-0.5 break-words text-sm text-surface-900",
                      field.mono && "font-mono",
                    )}
                  >
                    {field.value}
                  </dd>
                </div>
              </div>
            );
          })}
        </dl>
      ) : null}
    </SectionCard>
  );
}
